// Clamps a collection description to a few lines and reveals the rest on demand.
// The toggle stays hidden unless the text actually overflows, so short categories
// do not get a "Read more" that expands nothing.
class CollectionDescription extends HTMLElement {
  connectedCallback() {
    this.text = this.querySelector('[data-description-text]');
    this.toggle = this.querySelector('[data-description-toggle]');
    if (!this.text || !this.toggle) return;

    this.moreLabel = this.toggle.dataset.labelMore || this.toggle.textContent.trim();
    this.lessLabel = this.toggle.dataset.labelLess || this.moreLabel;

    this.toggle.addEventListener('click', () => this.onToggle());

    this.measure();
    // Fonts land after first paint and a narrower viewport reflows the text,
    // so the measurement is repeated rather than taken once.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.measure());
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.text);
  }

  // Publishes the text's real height for the open state to transition to. A ceiling
  // far above the content would be reached long after the text stopped growing, so
  // the reveal would look instant while the collapse looked smooth.
  //
  // Measuring lifts the height cap, which resets a running transition — so this runs
  // on layout changes only, never while a collapse animation is running.
  measure() {
    if (this.classList.contains('is-expanded') || this.measurePaused) return;
    const previous = this.text.style.maxHeight;
    this.text.style.maxHeight = 'none';
    const full = this.text.scrollHeight;
    this.text.style.maxHeight = previous;
    this.style.setProperty('--collection-description-full', `${full}px`);
    this.checkOverflow();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.endCollapseAnimation();
  }

  checkOverflow() {
    if (this.classList.contains('is-expanded')) return;
    const overflows = this.text.scrollHeight > this.text.clientHeight + 1;
    this.toggle.hidden = !overflows;
    this.classList.toggle('has-overflow', overflows);
  }

  onToggle() {
    const expanded = this.classList.toggle('is-expanded');
    this.toggle.textContent = expanded ? this.lessLabel : this.moreLabel;
    this.toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    if (!expanded) this.beginCollapseAnimation();
    else this.endCollapseAnimation();
  }

  beginCollapseAnimation() {
    this.endCollapseAnimation();
    this.measurePaused = true;

    this.onCollapseEnd = (event) => {
      if (event.target !== this.text || event.propertyName !== 'max-height') return;
      this.finishCollapseAnimation();
    };

    this.text.addEventListener('transitionend', this.onCollapseEnd);

    // Reduced-motion themes disable the CSS transition, so transitionend never fires.
    this.collapseFallbackTimer = setTimeout(() => this.finishCollapseAnimation(), 400);
  }

  finishCollapseAnimation() {
    if (!this.measurePaused) return;
    this.endCollapseAnimation();
    this.measurePaused = false;
    this.checkOverflow();
  }

  endCollapseAnimation() {
    clearTimeout(this.collapseFallbackTimer);
    if (this.onCollapseEnd) {
      this.text.removeEventListener('transitionend', this.onCollapseEnd);
      this.onCollapseEnd = null;
    }
  }
}

customElements.define('collection-description', CollectionDescription);
